import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CreditCard, Landmark, Vote, Trophy, Activity, Calendar, MessageSquare, ArrowRight, Clock, CheckCircle2, ShieldAlert } from "lucide-react";
import { trophiesApi, financeApi, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageContainer, SectionTitle, EmptyState, StatusBadge, UserAvatar } from "@/components/shared";
import { Reveal, StaggerGroup, StaggerItem, AnimatedCounter, SkeletonList } from "@/components/motion";
import { hoverLift } from "@/lib/motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const QUICK = [
  { to: "/sports", label: "Sports", icon: Activity },
  { to: "/calendrier", label: "Calendrier", icon: Calendar },
  { to: "/cotisations", label: "Cotisations", icon: CreditCard },
  { to: "/social", label: "Réseau", icon: MessageSquare },
];

export default function DashboardPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [votes, setVotes] = useState([]);
  const [showcase, setShowcase] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interacOpen, setInteracOpen] = useState(false);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [v, sc, lb] = await Promise.all([trophiesApi.votes(), trophiesApi.userShowcase(user.id), trophiesApi.leaderboard({ period: "week" })]);
      setVotes(v.data); setShowcase(sc.data); setLeaderboard(lb.data.slice(0, 5));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); }, [user?.id]);

  const isActive = user?.status === "active";
  const canVote = isActive || user?.role === "admin" || user?.role === "coach";

  const vote = async (voteId, candidateId) => {
    try { await trophiesApi.vote(voteId, candidateId); toast.success("Vote enregistré !"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const payAdhesionCard = async () => {
    setBusy(true);
    try { const { data } = await financeApi.checkout({ purpose: "adhesion", origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const payAdhesionInterac = async () => {
    if (!reference.trim()) { toast.error("Référence requise"); return; }
    setBusy(true);
    try { await financeApi.interac({ purpose: "adhesion", reference }); toast.success("Référence soumise"); setInteracOpen(false); refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <PageContainer>
      <Reveal>
        <Card className="overflow-hidden">
          <div className="relative overflow-hidden asl-hero-gradient asl-noise p-5 text-white">
            <div className="pointer-events-none absolute -right-6 -top-6 h-32 w-32 rounded-full bg-secondary/20 blur-2xl" />
            <div className="relative flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <UserAvatar user={user} size="h-14 w-14" className="ring-2 ring-white/20" />
                <div><div className="font-heading text-xl font-bold">Bonjour, {user.first_name} !</div><div className="mt-1"><StatusBadge status={user.status} /></div></div>
              </div>
              <Button variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white/15" onClick={() => navigate("/profil")} data-testid="dashboard-profil-bouton">Mon profil</Button>
            </div>
          </div>
          <CardContent className="p-5">
            {user.status === "pending_validation" && (
              user.adhesion_paid ? (
                <div className="flex items-center gap-2 text-sm text-amber-700"><Clock className="h-4 w-4" /> Votre paiement d'adhésion est reçu. En attente de validation par un administrateur.</div>
              ) : (
                <div className="space-y-3">
                  <div className="text-sm text-muted-foreground">Pour finaliser votre inscription, réglez les frais d'adhésion de <b>15$ CAD</b>.</div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={payAdhesionCard} disabled={busy} className="bg-secondary hover:bg-emerald-600 text-white" data-testid="dashboard-adhesion-carte"><CreditCard className="mr-1.5 h-4 w-4" />Payer par carte</Button>
                    <Button variant="outline" onClick={() => { setReference(""); setInteracOpen(true); }} data-testid="dashboard-adhesion-interac"><Landmark className="mr-1.5 h-4 w-4" />Virement Interac</Button>
                  </div>
                </div>
              )
            )}
            {user.status === "inactive" && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">Vous êtes <b>Membre inactif</b>. Payez une cotisation de session pour devenir <b>Membre actif</b> et accéder à tout (réseau social, votes, trophées).</div>
                <Button onClick={() => navigate("/cotisations")} className="bg-secondary hover:bg-emerald-600 text-white" data-testid="dashboard-cotiser-bouton">Voir les cotisations <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              </div>
            )}
            {user.status === "active" && (
              <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Vous êtes Membre actif — accès complet à la plateforme.</div>
            )}
            {(user.status === "sanctioned" || user.status === "banned") && (
              <div className="flex items-center gap-2 text-sm text-rose-700"><ShieldAlert className="h-4 w-4" /> Votre compte fait l'objet d'une sanction{user.sanction?.reason ? ` : ${user.sanction.reason}` : ""}. Certaines fonctionnalités sont restreintes.</div>
            )}
          </CardContent>
        </Card>
      </Reveal>

      <StaggerGroup className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {QUICK.map((q) => (
          <StaggerItem key={q.to}>
            <motion.div {...hoverLift}>
              <Card className="cursor-pointer" onClick={() => navigate(q.to)}><CardContent className="flex flex-col items-center gap-2 p-5"><div className="rounded-full bg-secondary/10 p-3"><q.icon className="h-5 w-5 text-secondary" /></div><span className="text-sm font-medium">{q.label}</span></CardContent></Card>
            </motion.div>
          </StaggerItem>
        ))}
      </StaggerGroup>

      {loading ? <SkeletonList count={3} /> : (
        <>
          {votes.length > 0 && (
            <section className="space-y-3">
              <SectionTitle>Votes de départage en cours</SectionTitle>
              {votes.map((vt) => (
                <Reveal key={vt.id}>
                  <Card className="border-secondary/40" data-testid="vote-carte"><CardContent className="p-5">
                    <div className="flex items-center gap-2"><Vote className="h-5 w-5 text-secondary" /><span className="font-heading font-semibold">{vt.type === "athlete" ? "Athlète" : "Entraîneur"} du mois — {vt.sport_name}</span></div>
                    <p className="mt-1 text-sm text-muted-foreground">Égalité à départager. {vt.total_votes} vote(s). {vt.has_voted ? "Vous avez déjà voté." : canVote ? "Sélectionnez votre choix :" : "Réservé aux membres actifs."}</p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {vt.options.map((o) => (
                        <Button key={o.user_id} variant="outline" disabled={vt.has_voted || !canVote} onClick={() => vote(vt.id, o.user_id)} data-testid="vote-option-bouton">{o.name}</Button>
                      ))}
                    </div>
                  </CardContent></Card>
                </Reveal>
              ))}
            </section>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <section className="space-y-3">
              <SectionTitle action={<button onClick={() => navigate("/profil")} className="text-sm text-secondary hover:underline">Vitrine</button>}>Mes trophées</SectionTitle>
              {showcase?.total ? (
                <Reveal><Card><CardContent className="flex flex-wrap gap-4 p-5">
                  <div className="flex items-center gap-3"><div className="rounded-full bg-trophy-gold/10 p-3"><Trophy className="h-6 w-6 text-trophy-gold" /></div><div><AnimatedCounter value={showcase.total} className="text-2xl font-heading font-bold tabular-nums" /><div className="text-xs text-muted-foreground">Trophées au total</div></div></div>
                </CardContent></Card></Reveal>
              ) : <EmptyState title="Aucun trophée" description="Participez et brillez pour être nommé !" icon={Trophy} />}
            </section>

            <section className="space-y-3">
              <SectionTitle>Palmarès (semaine)</SectionTitle>
              {leaderboard.length ? (
                <Reveal><Card><CardContent className="divide-y p-0">
                  {leaderboard.map((row, i) => (
                    <div key={row.recipient.id} className="flex items-center gap-3 p-3">
                      <span className="w-6 text-center font-heading font-bold text-muted-foreground">{i + 1}</span>
                      <UserAvatar user={row.recipient} size="h-8 w-8" />
                      <span className="flex-1 text-sm font-medium">{row.recipient.first_name} {row.recipient.last_name}</span>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold"><Trophy className="h-3.5 w-3.5 text-trophy-gold" />{row.count}</span>
                    </div>
                  ))}
                </CardContent></Card></Reveal>
              ) : <EmptyState title="Aucun trophée attribué" icon={Trophy} />}
            </section>
          </div>
        </>
      )}

      <Dialog open={interacOpen} onOpenChange={setInteracOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adhésion par virement Interac</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">Virement de <b>15,00 $ CAD</b> à :<br />Courriel : <b>informations.slps@gmail.com</b><br />Mot de passe : <b>adhesion</b></div>
            <div className="space-y-1.5"><Label>Référence de virement *</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="dashboard-interac-reference-input" /></div>
          </div>
          <DialogFooter><Button onClick={payAdhesionInterac} disabled={busy} data-testid="dashboard-interac-soumettre">{busy ? "…" : "Soumettre"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
